import React, { CSSProperties } from 'react';
import ChipLogo from 'assets/images/chip.png';
import { useGridMode } from 'contexts/GridModeContext';

/**
 * The felt's load state — a spinning HJ chip, flat and centered over the table.
 *
 * Shown while `game.tabletype` is empty (no gotTexas/gotOmaha yet) and reused
 * for the Tournament Final Balancing destination, which adds a caption.
 *
 * THIS is the loading state a grid cell actually shows. EN-10324's first pass
 * put a tile-sized loader behind `GameInner`'s `if (!currentTable)` guard, but
 * that guard cannot fire — `useTableSelector` returns an object on every path
 * (see pages/__tests__/gameCurrentTableGuard.test.tsx) — so the only loader a
 * player ever saw in a grid tile was this one, at its full-felt 72px. On a 4-up
 * tile that chip dominates the cell. Sizing it off `useGridMode` is what makes
 * the grid half of Marlon's change request (PR #3849) reach the screen.
 *
 * `useGridMode` is the right input here because this is a pure SIZING decision:
 * it is true for both surfaces that render a table smaller than the viewport —
 * MultiTableGrid's cells and the single-table carousel's tiled panels
 * (Game.tsx, `perView > 1`). Visibility is not this component's business; it
 * only renders when its own felt renders.
 *
 * Kept OUTSIDE the felt's 3D perspective tilt by its caller so the round chip
 * faces the camera instead of being squashed into an ellipse.
 */
const TableLoadIndicator = ({ balancing = false }: { balancing?: boolean }) => {
  const gridMode = useGridMode();
  const label = balancing ? 'Balancing Tables: Being moved to another table' : 'Loading table';

  return (
    <div style={overlayStyle} role='status' aria-live='polite' aria-label={label}>
      <style>{TABLE_LOAD_SPIN_KEYFRAMES}</style>
      <img src={ChipLogo} alt='' aria-hidden style={gridMode ? gridSpinnerStyle : spinnerStyle} />
      {balancing && <div style={captionStyle}>{label}</div>}
    </div>
  );
};

export default TableLoadIndicator;

export const TABLE_LOAD_SPIN_KEYFRAMES =
  '@keyframes hj-table-load-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}';

const overlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.85rem',
  zIndex: 6,
  pointerEvents: 'none'
};

const spinnerStyle: CSSProperties = {
  width: 72,
  height: 72,
  objectFit: 'contain',
  animation: 'hj-table-load-spin 1.1s linear infinite',
  filter: 'drop-shadow(0 0 12px rgba(233, 140, 38, 0.45))'
};

// A grid tile is a fraction of the viewport, so the full-felt 72px chip covers
// most of it. Same chip, same animation, same glow — just scaled to the surface
// it is loading, so the tile reads as "this table is coming" rather than as a
// giant spinner with a table hidden behind it.
const gridSpinnerStyle: CSSProperties = {
  ...spinnerStyle,
  width: 44,
  height: 44
};

// Caption paired with the chip loader on the balancing DESTINATION cell.
// Uses a text-shadow for legibility over the felt; no filled backdrop so the
// wording stays visually integrated with the load overlay instead of reading
// as a separate pill.
const captionStyle: CSSProperties = {
  maxWidth: '80%',
  textAlign: 'center',
  color: '#f1e3c7',
  fontWeight: 700,
  fontSize: '0.95rem',
  lineHeight: 1.3,
  textShadow: '0 1px 3px rgba(0,0,0,0.7)'
};
